/**
 * Channel Configuration Service - Multi-Tenant Architecture Foundation
 *
 * This module manages the multi-tenant channel system that powers the MDL Fan platform's
 * white-label sports streaming service. Each club/organisation operates as an isolated
 * tenant with complete control over their branding, features, pricing, and content.
 *
 * Key architectural decisions:
 * - Subdomain-based tenant isolation (e.g., bristolcity.matchday.live)
 *   WHY: Provides clear brand separation and allows independent SSL certificates per tenant
 * - Channel config determines EVERYTHING about the app experience
 *   WHY: Single source of truth prevents configuration drift across components
 * - Configuration cached aggressively via React Query
 *   WHY: Channel config rarely changes during a session (only on backend updates)
 *
 * Multi-tenant business model:
 * - Each sports club gets their own branded streaming platform without building infrastructure
 * - MDL provides the technology platform, clubs provide content and branding
 * - Revenue sharing model: platform fee + transaction percentage
 * - Clubs maintain their brand identity whilst leveraging shared technology
 *
 * Channel configuration controls:
 * - Visual Identity: Colours, logos, fonts, themes (complete white-labelling)
 * - Business Model: Available subscription plans, pricing tiers, promotional offers
 * - Feature Flags: Which features are enabled (live chat, highlights, stats, etc.)
 * - Content Access: Which fixtures, collections, and VOD content are available
 * - Third-party Integrations: Custom analytics, chatbots, support systems per tenant
 *
 * Security considerations:
 * - Channel resolution happens server-side based on request origin
 * - Prevents cross-tenant data access through API Gateway rules
 * - Each tenant's data is logically isolated in DynamoDB with tenant ID prefix
 *
 * @module apiChannel
 * @see {@link src/hooks/useChannel.js} - Primary consumer of channel configuration
 * @see {@link src/ui/ChannelThemeLoader.jsx} - Applies channel theming to UI
 * @see {@link .claude/api-integration-rules.md} - Multi-tenant API patterns
 * @see {@link docs/18_MULTI_TENANT_ARCHITECTURE.md} - Complete multi-tenant documentation
 */

import { getData } from './apiFetch';
import type { ChannelResponse } from '../types/api';

/**
 * Retrieves complete channel configuration for the current tenant.
 *
 * This is the most critical API call in the entire application as it determines:
 * - How the app looks (theme, branding, colours)
 * - What features are available (based on tenant's subscription tier)
 * - Which content can be accessed (fixtures, collections, VOD)
 * - Business rules (pricing, plans, promotional offers)
 *
 * WHY this architecture:
 * - Server determines channel from subdomain, preventing client-side spoofing
 * - Single API call provides all config, reducing round trips
 * - Config includes everything needed for app initialisation
 *
 * Business impact:
 * - Failed channel load = entire app unusable (intentional fail-fast)
 * - Cached for entire session to reduce API load
 * - Changes require page refresh to take effect (acceptable trade-off)
 *
 * The response includes:
 * - branding: Logo URLs, colour schemes, font preferences
 * - features: Enabled/disabled features based on subscription tier
 * - plans: Available subscription plans with pricing
 * - content: Accessible fixtures, collections, VOD libraries
 * - integrations: Third-party service configurations (Intercom, analytics)
 * - metadata: Channel name, description, social links, support contacts
 *
 * @returns {Promise<ChannelResponse>} Channel configuration object
 * @throws {Error} If channel doesn't exist or API fails
 *
 * @example
 * // Response structure for bristolcity channel
 * {
 *   id: "bristolcity",
 *   name: "Bristol City FC",
 *   theme_color: { primary: "#FF0000", secondary: "#FFFFFF" },
 *   logo_url: "https://...",
 *   subscription_plans: ["matchpass", "seasonpass"],
 *   features: { live_chat: true, highlights: true },
 *   // ... extensive configuration
 * }
 */
export async function getChannel(): Promise<ChannelResponse> {
  const path = 'channel';
  const data = await getData<ChannelResponse>({ path });
  return data;
}

/**
 * Retrieves only the channel ID without full configuration payload.
 *
 * WHY separate from getChannel():
 * - Performance optimisation for operations that only need tenant identification
 * - Reduces bandwidth when full config isn't needed (50 bytes vs 5KB)
 * - Used for API request headers, analytics tracking, logging context
 *
 * Business use cases:
 * - Analytics events need channel context but not visual theme
 * - API calls require X-Channel-Id header for tenant isolation
 * - Error logging needs channel ID for support ticket routing
 * - Performance monitoring tagged by channel for per-tenant metrics
 *
 * Server-side channel resolution:
 * - API Gateway extracts subdomain from request origin
 * - Validates against whitelist of registered channels
 * - Returns 404 if channel doesn't exist (prevents enumeration attacks)
 * - Used as partition key in DynamoDB for data isolation
 *
 * @returns {Promise<string>} Channel identifier (e.g., "bristolcity", "chesterfield")
 * @throws {Error} If channel doesn't exist or API fails
 *
 * @example
 * // Used in API headers for tenant context
 * const channelId = await getChannelId();
 * await postData({
 *   path: '/content/view',
 *   headers: { 'X-Channel-Id': channelId }
 * });
 */
export async function getChannelId(): Promise<string> {
  const config = {
    method: 'get' as const,
    path: 'domain',
  };
  const data = await getData<string>({ ...config });
  const channelId = data;

  return channelId;
}
